/**
 * POC animated lobby — the Canvas2D renderer (separate chunk, see EffectsLayer.tsx).
 *
 * One <canvas>, created only while a canvas effect plays and torn down with this component, so
 * there is no persistent backing store. Sized at min(devicePixelRatio, 2) — on a 390x844 phone the
 * 3x store would be ~12 MB per buffer, 2x halves it. A vanilla requestAnimationFrame loop drives
 * every active particle system and stops itself when the last particle dies.
 */

import React, { useEffect, useRef } from 'react';
import type { CanvasEffectInstance } from '../effectsBus';
import { effectsBus } from '../effectsBus';
import styles from '../EffectsLayer.module.css';
import type { ParticleSystem } from './particles';
import { createParticleSystem } from './particles';

interface CanvasEffectsProps {
  effects: readonly CanvasEffectInstance[];
}

const MAX_DPR = 2;

const sizeCanvas = (canvas: HTMLCanvasElement): { width: number; height: number; dpr: number } => {
  const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  return { width, height, dpr };
};

const CanvasEffects: React.FC<CanvasEffectsProps> = ({ effects }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const systemsRef = useRef(new Map<number, ParticleSystem>());
  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });

  // Start a system for every effect we have not seen; drop the ones the bus cancelled.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (sizeRef.current.width === 0) sizeRef.current = sizeCanvas(canvas);

    const systems = systemsRef.current;
    const ids = new Set(effects.map((effect) => effect.id));
    systems.forEach((_, id) => {
      if (!ids.has(id)) systems.delete(id);
    });
    effects.forEach((effect) => {
      if (systems.has(effect.id)) return;
      systems.set(
        effect.id,
        createParticleSystem({
          kind: effect.name === 'coinShower' ? 'coins' : 'confetti',
          width: sizeRef.current.width,
          height: sizeRef.current.height,
          seed: effect.payload.seed,
        }),
      );
    });

    if (frameRef.current !== null || systems.size === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const tick = (now: number): void => {
      frameRef.current = null;
      const dt = lastTimeRef.current === 0 ? 16 : now - lastTimeRef.current;
      lastTimeRef.current = now;

      const { width, height, dpr } = sizeRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);

      systems.forEach((system, id) => {
        system.step(dt);
        if (system.alive) {
          system.draw(ctx);
        } else {
          systems.delete(id);
          effectsBus.finish(id);
        }
      });

      if (systems.size > 0) {
        frameRef.current = window.requestAnimationFrame(tick);
      } else {
        lastTimeRef.current = 0;
      }
    };

    lastTimeRef.current = 0;
    frameRef.current = window.requestAnimationFrame(tick);
  }, [effects]);

  useEffect(() => {
    const onResize = (): void => {
      const canvas = canvasRef.current;
      if (canvas) sizeRef.current = sizeCanvas(canvas);
    };
    window.addEventListener('resize', onResize);
    const systems = systemsRef.current;
    return () => {
      window.removeEventListener('resize', onResize);
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      systems.clear();
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.canvas} />;
};

export default CanvasEffects;
